/**
 * URL sanitization for the rich-text editor's link dialog.
 *
 * Lives in its own module so this security-critical primitive can be
 * unit-tested in isolation. Used to gate href insertion against a strict
 * protocol allowlist.
 */

/**
 * Protocols permitted in editor links. Anything else (e.g. `javascript:`,
 * `data:`, `file:`) is rejected by `sanitizeUrl` to prevent XSS via
 * crafted hrefs.
 */
export const SUPPORTED_URL_PROTOCOLS = ['http:', 'https:', 'mailto:', 'sms:', 'tel:'] as const;
const SUPPORTED_URL_PROTOCOL_SET = new Set<string>(SUPPORTED_URL_PROTOCOLS);

/** User-facing message for the link dialog when `sanitizeUrl` returns null. */
export const URL_ERROR_MESSAGE = `Enter a valid URL (${SUPPORTED_URL_PROTOCOLS
  .map((p) => p.replace(':', ''))
  .join(', ')}).`;

/**
 * Normalize a user-entered URL.
 *
 * - Trims surrounding whitespace.
 * - Auto-prefixes `https://` when no scheme is present (so users can type
 *   `example.com`).
 * - Returns `null` for empty input, malformed URLs, or non-allowlisted
 *   protocols.
 */
export function sanitizeUrl(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;
  // Auto-prefix bare hostnames; leave anything that already declares a scheme alone.
  const candidate = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(candidate);
    return SUPPORTED_URL_PROTOCOL_SET.has(parsed.protocol) ? parsed.toString() : null;
  } catch {
    return null;
  }
}
